// Pure data: which sfx clip plays for each cross-cutting game event, plus the
// full list of clip keys (used by assetManifest.mjs to build the staged-loading
// manifest). No Phaser here so it stays unit-testable under plain node.

export const SFX_KEYS = [
  'coin_sell', 'coin_spend', 'plant', 'harvest', 'crop_ready', 'place',
  'animal_kura', 'animal_krowa', 'animal_pies', 'animal_kot', 'collect_product',
  'click', 'whoosh', 'error', 'bike_bell', 'message_in', 'upgrade_fanfare',
  // Game Hall / board games (scene-local playSfx calls)
  'move_piece', 'capture_piece', 'promote_piece', 'game_start',
  'game_win', 'game_draw', 'sit_chair', 'place_board_game',
  'chess_move', 'chess_capture', 'mancala_sow', 'mancala_capture',
  'backgammon_move', 'backgammon_hit', 'backgammon_roll',
  // Farm 6 (farm sounds; the moo / cluck and hen feeding come in variants, one picked at random)
  'animal_krowa_2', 'animal_krowa_3', 'animal_kura_2', 'animal_kura_3',
  'feed_krowa', 'feed_kura', 'feed_kura_2', 'pet_krowa', 'pet_krowa_2', 'pet_kura',
  'plow', 'zasyp', 'water', 'tractor', 'seeder', 'harvester', 'farm_day', 'farm_night',
  // the home pets (real CC0 recordings; the owner's picks)
  'pet_kot', 'pet_kot_2', 'pet_pies', 'pet_pies_2', 'pet_pies_3', 'animal_kot_2', 'animal_kot_3',
  // an animal trampling a crop (real CC0 recordings, the owner's four picks played at random)
  'trample', 'trample_2', 'trample_3', 'trample_4',
  // a flock passing over the garden (real CC0 recordings, the owner's two picks played at random)
  'birds_pass', 'birds_pass_2',
];

export const MUSIC_KEY = 'music_loop';

// Event names emitted by gameState / client / realtime -> sfx clip key. Not every
// clip in SFX_KEYS appears here — scene-local sounds (animal_*, collect_product,
// click, whoosh, bike_bell, crop_ready) are triggered by direct playSfx() calls.
export const EVENT_SFX = {
  sell: 'coin_sell',
  buy: 'coin_spend',
  plant: 'plant',
  harvest: 'harvest',
  place: 'place',
  upgrade: 'upgrade_fanfare',
  error: 'error',
  message_in: 'message_in',
  collect: 'collect_product',   // eggs / milk from a farm animal
  moo: ['animal_krowa', 'animal_krowa_2', 'animal_krowa_3'],    // a cow's call when near (herd) — a voice at random
  cluck: ['animal_kura', 'animal_kura_2', 'animal_kura_3'],     // a hen's call
  // Farm 6: feeding / petting have their own sounds (the moo / cluck there sounded weird — owner)
  feed_krowa: 'feed_krowa',
  feed_kura: ['feed_kura', 'feed_kura_2'],
  pet_krowa: ['pet_krowa', 'pet_krowa_2'],
  pet_kura: 'pet_kura',
  pet_kot: ['pet_kot', 'pet_kot_2'],            // a purr
  pet_pies: ['pet_pies', 'pet_pies_2', 'pet_pies_3'],
  meow: ['animal_kot', 'animal_kot_2', 'animal_kot_3'],
  bark: 'animal_pies',
  trample: ['trample', 'trample_2', 'trample_3', 'trample_4'],   // the server's herd_sound when a crop is trampled
  plow: 'plow',
  zasyp: 'zasyp',
  water: 'water',               // a friend's Podlej
  vehicle_tractor: 'tractor',
  vehicle_seeder: 'seeder',
  vehicle_harvester: 'harvester',
  birds: ['birds_pass', 'birds_pass_2'],   // a flock passing over the garden (backdropFx createBirds)
};

// Which event a farm animal op sounds as (farm 6).
export function animalSoundEvent(op, species) {
  if (op === 'collect_animal') return 'collect';
  if (op === 'feed_animal') return species === 'krowa' ? 'feed_krowa' : species === 'kura' ? 'feed_kura' : voiceEvent(species);
  return ['krowa', 'kura', 'kot', 'pies'].includes(species) ? `pet_${species}` : 'pet_kura';
}

// An animal's own voice as an event (the variants are picked at random).
export const voiceEvent = (species) => ({ krowa: 'moo', kura: 'cluck', kot: 'meow', pies: 'bark' })[species] ?? null;

// Playback volume per clip. Every clip plays at SFX_VOLUME except those mastered much louder
// than the rest: upgrade_fanfare.mp3 averages -15 dB where the others sit at -28…-40 dB, so a
// vehicle purchase or level-up blared over everything (owner, 2026-09-29).
export const SFX_VOLUME = 0.6;
// The farm's collect chime sits under the coin sound (owner 2026-09-30: much quieter). The farm-6
// clips (the animal voices included) were made at the common loudness, -35 LUFS: no gain needed.
// Birds overhead are background, so they sit a little under the field work.
const SFX_GAIN = { upgrade_fanfare: 0.23, collect_product: 0.5, birds_pass: 0.7, birds_pass_2: 0.7 };
export function sfxVolume(key) { return SFX_VOLUME * (SFX_GAIN[key] ?? 1); }

// A clip for an event; a list of variants gives one at random (`rnd` injectable for tests).
export function sfxForEvent(name, rnd = Math.random) {
  const v = EVENT_SFX[name];
  if (Array.isArray(v)) return v[Math.min(v.length - 1, Math.floor(rnd() * v.length))];
  return v ?? null;
}

// The farm's ambience loop by day / at night (farm 6).
export const ambienceFor = (night) => (night ? 'farm_night' : 'farm_day');
